/**
 * Invite — a pending member invitation. Signing in with the invited address
 * announces it (`users/{uid}/notifications/{token}`, notification.entity.ts);
 * nothing joins the person until `acceptInvitation` writes the grant.
 *
 * Path: `workspaces/{orgId}/invites/{token}` — an invite is workspace-owned
 * data, so it nests under its workspace like every sibling collection
 * (members, roles, events, …); the doc id is a 128-bit hex token,
 * `crypto.randomBytes(16)` (momo-starter verbatim), the correlation key
 * across sendOTP/mintSession, the pending member-mirror row and the
 * invitee's notification — NOT the secret; the OTP code is what proves
 * inbox possession.
 *
 * No `read` action: momo-starter's invitee-email read powers FE signup
 * discovery; here sendOTP and mintSession resolve the invite server-side,
 * the invitee reads its announcement off their own notifications, and
 * admins read pending rows off the Member mirror — the smaller,
 * default-deny surface.
 *
 * Fields justified:
 *   token          — doc id; the correlation key.
 *   email          — invitee address; drives sendOTP/verifyOTP admission.
 *   displayName    — single field (the repo's canonical identity field, not
 *                    momo-starter's firstName/lastName fold) — member-list
 *                    display + email greeting only; the member's own
 *                    displayName is collected fresh at first-sign-in consent.
 *   roleId         — FK to the org role catalog.
 *   invitedBy      — uid; `createInvite` is caller-authenticated, so an
 *                    in-app actor exists to attribute this to.
 *   createdAt / expiresAt — Timestamp; expiry = createdAt + 14 days (momo-starter).
 *   status         — 'pending' → 'redeemed' (the invitee accepted) |
 *                    'declined' (the invitee turned it down) | 'revoked' (an
 *                    admin withdrew it) | 'expired'. `declined` is its own
 *                    member, never a reuse of `revoked`: a withdrawal the
 *                    invitee had no part in must not read back as their own
 *                    refusal.
 *   scope          — GrantScopeSchema, reused — never re-declared.
 *   redeemedBy     — nullish uid, stamped by acceptInvitation on redemption.
 *   emailDispatch  — nullish delivery stamp — the operator's only signal that
 *                    the invite email sent.
 *   founding       — true on the `owner` invitation a staff approval founds
 *                    an organization with (foundWithOwnerInvite,
 *                    founderReinvite): the operator's offer, whose accept
 *                    concludes the founder's contract with the operator and
 *                    so owes the operator's platform terms. Written by those
 *                    two alone; absent on every invitation a member sends.
 *
 * Excluded:
 *   orgId — the parent path segment already carries it; the doc itself never
 *           restates it, matching every other workspace-owned entity
 *           (Member, Role, Event, …). `createInvite`/`revokeInvite` resolve
 *           it from `CLIENT.orgId` server-side, never from a stored field.
 */
import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import type { Timestamp } from "firebase/firestore";
import { z } from "zod";
import { GrantScopeSchema } from "./grant.entity.ts";
import type { Actor } from "./workspace.entity.ts";
import { Id } from "./workspace.entity.ts";

/** How long an invitation stays answerable after it is written. */
export const INVITE_EXPIRY_MS = 14 * 24 * 60 * 60 * 1000;

export const InviteSchema = z.object({
	token: Id,
	email: z.string().email(),
	displayName: z.string().min(1),
	roleId: Id,
	invitedBy: Id,
	createdAt: z.custom<Timestamp>(),
	expiresAt: z.custom<Timestamp>(),
	status: z.enum(["pending", "redeemed", "declined", "revoked", "expired"]),
	scope: GrantScopeSchema,
	redeemedBy: Id.nullish(),
	founding: z
		.boolean()
		.nullish()
		.describe(
			"the owner invitation a staff approval founded the organization with",
		),
	emailDispatch: z
		.object({
			providerMessageId: z.string().nullish(),
			acceptedAt: z.custom<Timestamp>().nullish(),
			error: z.string().nullish(),
		})
		.nullish(),
});
export type Invite = z.infer<typeof InviteSchema>;

/**
 * The invite as the invitee may see it — display strings only, resolved
 * server-side from the raw fields (roleId → the role catalog's name,
 * invitedBy → the inviter's own displayName, or the published platform
 * operator for a founding offer without a member profile): no raw id crosses to a person
 * who holds no grant at the organization yet. Carried by sendOTP's answer,
 * so the wizard can say what the code joins before it is typed, and by the
 * invitee's notification, so the app can say what Accept joins.
 */
export const InviteProjectionSchema = z.object({
	workspaceName: z
		.string()
		.min(1)
		.describe("the org/brand name the invitee is joining"),
	roleName: z
		.string()
		.min(1)
		.describe(
			"the invite's role catalog name, resolved server-side from roleId",
		),
	invitedBy: z
		.string()
		.min(1)
		.describe(
			"the inviting member's display name or founding operator's published name",
		),
});
export type InviteProjection = z.infer<typeof InviteProjectionSchema>;

// No `read`/`list` action — see docblock; default-deny covers both.
// `create` is server-only: `.requiresHandler('createInvite')` denies every
// FE-direct create, forcing the callable whose own spine (auth non-null →
// caller holds members.invite → role exists → role-power containment → scope
// tenancy → resolve-never-create) performs the multi-doc authorization no
// declarative rule can express.
// `revoke` is server-only too: a Firestore `.when(...)` rule CAN now resolve
// `hasPermission(...)` + `me.mfa` + the doc's own `status` off the
// workspace-nested path, but target-role containment — the invite's OWN
// role must be ⊆ the caller's own permissions — is a cross-document
// comparison no declarative rule can express, the same shape Grant/Role's
// own `.requiresHandler` blocks stand on. `revokeInvite.api.ts` (onCall)
// runs that check server-side, then flips a `pending` invite to `revoked`;
// it is the sole entry point.
// `edit` is server-only for the same reason: patching `roleId` to a NEW role
// re-runs createInvite's own role-existence + role-power containment (the
// new role's permissions must be ⊆ the caller's own) — a cross-document
// comparison no declarative rule can express. `editInvite.api.ts` (onCall)
// runs that check server-side, then patches only the supplied fields on a
// still-`pending` invite; it is the sole entry point. Exists so a
// mis-assigned role or a typo'd name is corrected in place, without the
// token churn and re-sent email a revoke+recreate forces.
// `accept` and `decline` are the invitee's two answers, server-only too: the
// authority is being the addressee — the caller's proven address matches the
// invite's `email` — and the caller holds no grant at this organization yet,
// so no declarative rule evaluated under their permissions can admit the
// write. `acceptInvitation.api.ts` writes the grant, `declineInvitation.api.ts`
// sets `declined`; each re-reads the invite inside its transaction so a
// revoke wins a race.
export const Invite = entity<Actor>()({
	schema: InviteSchema,
	actions: ["create", "revoke", "edit", "accept", "decline"] as const,
	namespace: "tickets.invites",
})
	.pipe(({ can, and }) => [
		can("create")
			.when(() => and())
			.requiresHandler("createInvite"),
		can("revoke")
			.when(() => and())
			.requiresHandler("revokeInvite"),
		can("edit")
			.when(() => and())
			.requiresHandler("editInvite"),
		can("accept")
			.when(() => and())
			.requiresHandler("acceptInvitation"),
		can("decline")
			.when(() => and())
			.requiresHandler("declineInvitation"),
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/invites/{token}",
		}),
	);
